import React, { useEffect, useRef, useState } from "react";
import {
  ListItemProps,
  MultiSelectChangeEvent
} from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { Grid, GridColumn as Column, GridCellProps, GridExpandChangeEvent, GridDetailRowProps } from '@progress/kendo-react-grid';
import { Switch, SwitchChangeEvent } from '@progress/kendo-react-inputs';
import { ReasonCodeInstance } from "../../../types/call/CallResultReasonCode";
import { CallResult } from "../../../types/call";
import { Scorecard } from "../../../types/scorecard";
import {
  DefaultScorecardAndAutoQARules,
  AutoQARule,
  NewAutoQARule,
  UserAllocatedAutoQARule,
} from "../../../types/automatedQA/AutomatedQA";
import autoqarulesService from "../../../services/autoqarules.service";
import { SweetAlertOptions } from "sweetalert2";
import useSwal from "../../../hooks/useSwal";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import useMasterData from "../../../hooks/useMasterData";
import { TinyUser, User } from "../../../types/user";
import CustomUserGridInfo from "../../../components/custom/grid/CustomUserGridInfo";
import CustomMultiSelect from "../../../components/custom/form/CustomMultiSelect";
import CustomComboBox from "../../../components/custom/form/CustomComboBox";
import UpsertAutoQARule from "./UpsertAutoQARule";
import { Loader } from "@progress/kendo-react-indicators";
import { AxiosError } from "axios";
import useTranslation from "../../../hooks/useTranslation";
import useLocale from "../../../hooks/useLocale";
import CustomSearchFieldTextInput from "../../../components/custom/form/CustomSearchFieldTextInput";
import { Error } from "@progress/kendo-react-labels";

interface AssignScorecardProps {
  callResults: CallResult[];
  scorecards: Scorecard[];
  automatedQARules: AutoQARule[];
  setAutomatedQARules:React.Dispatch<React.SetStateAction<AutoQARule[]>>;
  setView: React.Dispatch<React.SetStateAction<"autoqa-list" | "assign-scorecard">>;
  view:"autoqa-list" | "assign-scorecard";
  updateUserAllocatedAutoQARulesData?:UserAllocatedAutoQARule;
  setUpdateUserAllocatedAutoQARulesData:React.Dispatch<React.SetStateAction<UserAllocatedAutoQARule | undefined>>;
  fetchAllUserAllocatedAutoQARules:(appendLastData?: UserAllocatedAutoQARule) => Promise<void>;
  handleExpandChange:(e: GridExpandChangeEvent) => void;
  qaruleSearch:string;
  setQaruleSearch:React.Dispatch<React.SetStateAction<string>>;
  onQaRuleSearchChange:(e: React.FormEvent<HTMLInputElement>) => void;
  userAllocatedAutoQARules:UserAllocatedAutoQARule[];
}

const AssignScorecard: React.FC<AssignScorecardProps> = ({
  callResults,
  scorecards,
  automatedQARules,
  setAutomatedQARules,
  setView,
  updateUserAllocatedAutoQARulesData,
  setUpdateUserAllocatedAutoQARulesData,
  fetchAllUserAllocatedAutoQARules,
  handleExpandChange,
  qaruleSearch,
  setQaruleSearch,
  onQaRuleSearchChange,
  userAllocatedAutoQARules
}) => {
  const editUserAllocatedQARuleIds:number[]=updateUserAllocatedAutoQARulesData?.autoQARules.map((rule:AutoQARule)=>rule.id)??[];
  const trans = useTranslation("AssignScorecard");
  const localeCtx = useLocale();
  const masterData = useMasterData();
  const swal = useSwal();
  const formRef = useRef<any>();

  const [selectedAutomatedQARulesList, setSelectedAutomatedQARulesList] = useState<AutoQARule[]>(updateUserAllocatedAutoQARulesData ? updateUserAllocatedAutoQARulesData.autoQARules: []);
  const [updateAutomatedQARuleData, setUpdateAutomatedQARuleData]=useState<AutoQARule>();
  const [advancedSettingsexpanded, setAdvancedSettingsexpanded] = useState<boolean>(updateUserAllocatedAutoQARulesData ? true : false);
  const [showUpsertAutoQARuleDialog, setShowUpsertAutoQARuleDialog] = useState<boolean>(false);
  const [isChanged,setIsChanged]=useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [usersWithSameAllocationCriteria,setUsersWithSameAllocationCriteria] = useState<TinyUser[]>([]);

  useEffect(() => {
    if (!localeCtx?.selectedLocale?.current.componentTranslations["AssignScorecard"]) {
      trans.fetchTranslations("AssignScorecard");
    }
  }, [localeCtx?.selectedLocale]); 

  useEffect(()=>{
    if(updateUserAllocatedAutoQARulesData){
      setAutomatedQARules(automatedQARules.filter((rule:AutoQARule)=>editUserAllocatedQARuleIds.includes(rule.id)).concat(automatedQARules.filter((rule:AutoQARule)=>!editUserAllocatedQARuleIds.includes(rule.id))))
    }
  },[])

  useEffect(()=>{
    return ()=>{
      setUpdateUserAllocatedAutoQARulesData(undefined)
    }
  },[])

  useEffect(()=>{
    compareChanges();
  },[updateUserAllocatedAutoQARulesData,selectedAutomatedQARulesList])

  const onOpenUpsertAutoQARuleModal = () => {
    setShowUpsertAutoQARuleDialog(true);
  }
  const onCloseUpsertAutoQARuleModal = () => {
    setUpdateAutomatedQARuleData(undefined);
    setShowUpsertAutoQARuleDialog(false);
  }

  const cancelClickHandler=(formRenderProps : FormRenderProps)=>{
    setUpdateUserAllocatedAutoQARulesData(undefined)
    setSelectedAutomatedQARulesList([]);
    formRenderProps.onChange("userFormData", {
      value: [],
    });
    formRenderProps.onChange("scorecardFormData", {
      value: undefined,
    });
    setSelectedAutomatedQARulesList([]);
    setView("autoqa-list")
  }

  const handleDeleteAutomatedQARuleFromDB = async (qaRuleData: AutoQARule) => {
    setLoader(true);
    try{
      const swalOptions: SweetAlertOptions<any, any> = {
        title: "Are your sure?",
        text: "This will delete the allocation criteria. Do you want to continue?",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "Cancel",
        confirmButtonText: "OK",
      };
      const response = await swal.fire(swalOptions);
      if(response.isConfirmed){
        await autoqarulesService.deleteAutoQARule(qaRuleData.id);
        const swalOptions: SweetAlertOptions<any, any> = {
          icon: "success",
          title: trans.fetchLabelKeyTranslation("SwalDeleteSuccessText","Allocation Criteria deleted successfully"),
          confirmButtonText: trans.fetchLabelKeyTranslation("SwalOkText","OK"),
        };
        swal.fire(swalOptions);
        const updatedSelectedAutomatedQARulesList: AutoQARule[] = selectedAutomatedQARulesList.filter(
          (qaRule: AutoQARule) => qaRule.id !== qaRuleData.id
        );
        fetchAllUserAllocatedAutoQARules(undefined);
        setAutomatedQARules(automatedQARules.filter((rule:AutoQARule)=>rule.id!==qaRuleData.id));
        setSelectedAutomatedQARulesList(updatedSelectedAutomatedQARulesList);
      }
    } catch(error){
      const swalOptions: SweetAlertOptions<any, any> = {
        icon: "error",
        title: trans.fetchLabelKeyTranslation("SwalDeleteFailureText","Allocation Criteria cannot be deleted right now. Please try again later."),
        confirmButtonText: trans.fetchLabelKeyTranslation("SwalOkText","OK"),
      };
      swal.fire(swalOptions);
    } finally{
      setLoader(false);
    }
  };

  const handleUpsertAutoQARuleSubmit = async (data: NewAutoQARule,autoQARuleId?:number) => {
    setLoader(true);
    try {
      if(autoQARuleId!==undefined){
        let response = await autoqarulesService.updateAutoQARule(data,autoQARuleId);
        setAutomatedQARules(automatedQARules.map((rule:AutoQARule)=>{
          if(rule.id===response.id){
            return response;
          }
          return rule;
        }))
        const updatedSelectedAutomatedQARulesList = selectedAutomatedQARulesList.map((rule:AutoQARule)=>{
          if(rule.id===response.id){
            return response;
          }
          return rule;
        })
        setSelectedAutomatedQARulesList(updatedSelectedAutomatedQARulesList)
      } else {
        let response = await autoqarulesService.createNewAutoQARule(data);
        setAutomatedQARules([response,...automatedQARules])
        setSelectedAutomatedQARulesList([...selectedAutomatedQARulesList,response]);
      }
      const swalOptions: SweetAlertOptions<any, any> = {
        icon: "success",
        title: autoQARuleId ? trans.fetchLabelKeyTranslation("SwalUpdateSuccessText","Allocation Criteria has been updated") : trans.fetchLabelKeyTranslation("SwalInsertSuccessText","A new Allocation Criteria has been created"),
        confirmButtonText: trans.fetchLabelKeyTranslation("SwalOkText","OK"),
      };
      setUpdateAutomatedQARuleData(undefined);
      compareChanges();
      setShowUpsertAutoQARuleDialog(false);
      await swal.fire(swalOptions);
    } catch (err) {
      if(err instanceof AxiosError){
        let errMssg = autoQARuleId ? trans.fetchLabelKeyTranslation("SwalUpdateFailureText","Allocation Criteria cannot be updated right now. Please try again later.") 
            : trans.fetchLabelKeyTranslation("SwalInsertFailureText","Allocation Criteria cannot be creted right now. Please try again later.");
        if(err.response?.data && err.response.data.error){
          errMssg = err.response.data.error;
        }
        const swalOptions: SweetAlertOptions<any, any> = {
          icon: "error",
          title: errMssg,
          confirmButtonText: trans.fetchLabelKeyTranslation("SwalOkText","OK"),
        };
        await swal.fire(swalOptions);
      }
    } finally{
      setLoader(false);
    }
  };

  const NewAutomatedQARuleSubmitHandler = async (formRenderProps:FormRenderProps) => {
    setLoader(true);
    const assignDefaultScorecardAndAutoQARulesData: DefaultScorecardAndAutoQARules[] = [];
    const form = formRef.current as Form;
    const users = form.values.userFormData;
    const defaultScorecardId = form.values.scorecardFormData?.id ?? 0;
    const autoQARuleIds = selectedAutomatedQARulesList.map(
      (qaRule: AutoQARule) => qaRule.id
    );
    for (let i = 0; i < users.length; i++) {
      assignDefaultScorecardAndAutoQARulesData.push({
        id:users[i].id,
        defaultScorecardId: defaultScorecardId,
        autoQARuleIds: autoQARuleIds
      })
    }
    try{
      const data = await autoqarulesService.assignDefaultScorecardAndAutomatedQARules(assignDefaultScorecardAndAutoQARulesData);
      const swalOptions: SweetAlertOptions<any, any> = {
        icon: "success",
        title: trans.fetchLabelKeyTranslation("SwalAssignedSuccessText","Allocation Criteria and Default Scorecard has been assigned."),
        confirmButtonText: trans.fetchLabelKeyTranslation("SwalOkText","OK"),
      };
      await swal.fire(swalOptions);
      if(!updateUserAllocatedAutoQARulesData){
        formRenderProps.onFormReset();
        setSelectedAutomatedQARulesList([]);
        setAdvancedSettingsexpanded(false);
      }else{
        setUpdateUserAllocatedAutoQARulesData(data[0]);
      }
      fetchAllUserAllocatedAutoQARules(updateUserAllocatedAutoQARulesData ? undefined : data[0]);
      setQaruleSearch("");
      setUsersWithSameAllocationCriteria([]);
    }catch(err){
      if(err instanceof AxiosError){
        let errMssg = trans.fetchLabelKeyTranslation("SwalAssignedFailureText","Allocation Criteria and Default Scorecard cannot be assigned. Please try again later.");
        if(err.response?.data && err.response.data.error){
          errMssg = err.response.data.error;
        }
        const swalOptions: SweetAlertOptions<any, any> = {
          icon: "error",
          title: errMssg,
          confirmButtonText: trans.fetchLabelKeyTranslation("SwalOkText","OK"),
        };
        await swal.fire(swalOptions);
      }
    } finally{
      setLoader(false);
    }
  };

  const itemRender = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const userInfo: TinyUser = {
      id: itemProps.dataItem.id,
      loginUserId: itemProps.dataItem.loginUserId,
      firstName: itemProps.dataItem.firstName,
      lastName: itemProps.dataItem.lastName,
      email: itemProps.dataItem.email,
      phoneNo: itemProps.dataItem.phoneNo,
      image: itemProps.dataItem.image,
      isArchived: itemProps.dataItem.isArchived,
    };
    const checkedclass = itemProps.selected ? "liChecked" : "";
    const itemChildren = (
      <span className={checkedclass}>
        <CustomUserGridInfo userInfo={userInfo} selected={itemProps.selected} />
      </span>
    );
    return React.cloneElement(li, li.props, itemChildren);
  }

  const CustomReasonCodesCell=(gridData: GridCellProps)=>{
    const rcs = gridData.dataItem.reasonCodes;
    return(
      <td>
        {rcs.length === 0 ? trans.fetchLabelKeyTranslation("AllReasonCodesText","All Reason Codes") : rcs.map((code: any) => code.name).join(", ")}
      </td>
    )
  }

  const ActionCell = (props: GridCellProps) => {
    const rule = props.dataItem;
    const editAutoQARuleHandler = () => {
      setUpdateAutomatedQARuleData(rule);
      setShowUpsertAutoQARuleDialog(true);
    }
    const qaRuleSwitchHandler=(event:SwitchChangeEvent)=>{
      if(event.value){
        const isValid = AutoQARuleValidator([...selectedAutomatedQARulesList,rule]);
        if(isValid){
          setSelectedAutomatedQARulesList([...selectedAutomatedQARulesList,rule])
        }else{
          const swalOptions: SweetAlertOptions<any, any> = {
            icon: "error",
            title: trans.fetchLabelKeyTranslation("SameConfigErrorMssg","You already have an Allocation Criteria selected with identical configurations. Kindly remove or modify the duplicate rules to proceed."),
            confirmButtonText: trans.fetchLabelKeyTranslation("SwalOkText","OK"),
          };
          swal.fire(swalOptions);
        }
      }else{
        setSelectedAutomatedQARulesList(selectedAutomatedQARulesList.filter((item:AutoQARule)=>item.id!==rule.id))
      }
      compareChanges();
    }
    return(
      <td>
        <div className="d-flex align-items-center justify-content-end">
          <span title="Enable" className="mr-3">
            <Switch
              checked={selectedAutomatedQARulesList.map((rule: AutoQARule) => rule.id).includes(rule.id)}
              onChange={qaRuleSwitchHandler}
            />
          </span>
          <div className="iBtn d-flex justify-content">
            <span className="fs-18 m-r-5" title="Edit" >
              <Button onClick={editAutoQARuleHandler} className="d-flex align-items-center justify-conter m-r-2 iconBtn">
                <i className="bi bi-pencil fs-17 line-height-1"></i>
              </Button>
            </span>
            <span className="fs-18" title="Delete">
              <Button onClick={() => handleDeleteAutomatedQARuleFromDB(rule)} className="d-flex align-items-center justify-conter iconBtn">
                <i className="bi bi-trash3 fs-17 line-height-1"></i>
              </Button>
            </span>
          </div>
        </div>
      </td>
    )
  }


  const DetailComponent = (props: GridDetailRowProps) => {
    const rule = props.dataItem;
    const editAutoQARuleHandler = () => {
      setUpdateAutomatedQARuleData(rule);
      setShowUpsertAutoQARuleDialog(true);
    }

    return (
      <div className="d-flex align-items-center justify-content-end">
        <div className="iBtn d-flex justify-content">
          <span className="fs-18 m-r-5" title="Edit" >
            <Button onClick={editAutoQARuleHandler} className="d-flex align-items-center justify-conter m-r-2 iconBtn">
              <i className="bi bi-pencil fs-17 line-height-1"></i>
            </Button>
          </span>
          <span className="fs-18" title="Delete">
            <Button onClick={() => handleDeleteAutomatedQARuleFromDB(rule)} className="d-flex align-items-center justify-conter iconBtn">
              <i className="bi bi-trash3 fs-17 line-height-1"></i>
            </Button>
          </span>
        </div>
      </div>

    )
  }

  const AutoQARuleValidator=(qaRulesList:AutoQARule[])=>{
    for(let i=0;i<qaRulesList.length-1;i++){
      const callResultId = qaRulesList[i].callResult.id ?? 0;
      const rCodes = qaRulesList[i].reasonCodes.map((rc: ReasonCodeInstance) => rc.id );
      for(let j=i+1;j<qaRulesList.length;j++){
        if(qaRulesList[j].callResult.id === callResultId &&
          (rCodes.length === 0 ||
            qaRulesList[j].reasonCodes.length === 0 ||
           qaRulesList[j].reasonCodes.filter((rc:ReasonCodeInstance)=>rCodes.includes(rc.id)).length > 0
          )
        ){
          return false;
        }
      }
    }
    return true;
  }

  const UserFieldValidator=(value:any)=>{
    if(value.length<=0)return trans.fetchLabelKeyTranslation("ValidationTextTwo","Please select an user")
    return ""
  }

  const DefaultScorecardFieldValidator=(value:any)=>{
    if(!value)return trans.fetchLabelKeyTranslation("ValidationTextThree","Please select a default scorecard")
    return ""
  }

  const compareArray = (array1: any[], array2: any[]): boolean => {
    array1.sort(function (a, b) {
      if (a.id > b.id) return 1;
      else return -1;
    });

    array2.sort(function (a, b) {
      if (a.id > b.id) return 1;
      else return -1;
    });

    if (array1.length != array2.length) return false;
    else {
      for (let i = 0; i < array1.length; i++) {
        if (array1[i].id != array2[i].id) return false;
      }
      return true;
    }
  };

  const compareChanges=()=>{
    const form = formRef.current as Form;
    if(updateUserAllocatedAutoQARulesData){
      if (
        compareArray(
          updateUserAllocatedAutoQARulesData?.autoQARules
            ?.map((u) => {
              return u;
            })
            .slice() ?? [],
          selectedAutomatedQARulesList
        ) &&
        form.values.scorecardFormData?.scorecardName ==
          updateUserAllocatedAutoQARulesData?.defaultScoreCard?.scorecardName
      ) {
        setIsChanged(false);
      } else {
        setIsChanged(true);
      }
    }
  }

  return (
    <div className="p-3 add-qa">
      <Form
        ref={formRef}
        initialValues={{
          userFormData:updateUserAllocatedAutoQARulesData ? [updateUserAllocatedAutoQARulesData.user] : [],
          scorecardFormData: updateUserAllocatedAutoQARulesData ? updateUserAllocatedAutoQARulesData.defaultScoreCard : undefined,
          automatedQARulesFormData: updateUserAllocatedAutoQARulesData ? updateUserAllocatedAutoQARulesData.autoQARules : [],
        }}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <h5 className="fs-15 text-black-14">{trans.fetchLabelKeyTranslation("EnableAutoQATitle","Enable Automated QA for your recorded users")}</h5>
            <div className="row mb-3">
              <div className="col-md-6">
                <label>{trans.fetchLabelKeyTranslation("SelectUserFieldTitle","Select User")}</label>
                <Field
                  id="userFormData"
                  name="userFormData"
                  data={masterData?.standardUsers.filter(
                    (u: User) => !u.isArchived && u.licensePackage?.baseLicense.id===3
                  )}
                  value={formRenderProps.valueGetter("userFormData")}
                  itemRender={itemRender}
                  textField="email"
                  filterFields={["email", "firstName", "lastName"]}
                  filtering={true}
                  sorting={true}
                  sortField={"email"}
                  component={CustomMultiSelect}
                  onChange={(e:MultiSelectChangeEvent)=>{
                    const findUsersWithSameAllocationCriteria:TinyUser[]=[];
                    const users = e.value;
                    users.forEach((u:TinyUser)=>{
                      const findUserInUserAllocatedAutoQARules = userAllocatedAutoQARules.find((allocatedRule:UserAllocatedAutoQARule)=>allocatedRule.user.id == u.id);
                      if(findUserInUserAllocatedAutoQARules)findUsersWithSameAllocationCriteria.push(findUserInUserAllocatedAutoQARules.user);
                    })
                    setUsersWithSameAllocationCriteria(findUsersWithSameAllocationCriteria);
                    compareChanges();
                  }}
                  disabled={updateUserAllocatedAutoQARulesData}
                  validator={UserFieldValidator}
                  style={{minHeight: '38px'}}
                />
                <div className="m-t-2">
                  {usersWithSameAllocationCriteria.length > 0 && (
                    <Error>
                      {usersWithSameAllocationCriteria.map((user: TinyUser) => user.email).join(", ")} has auto QA assigned to them. Saving these changes will overwrite their existing auto QA configuration.
                    </Error>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <label>{trans.fetchLabelKeyTranslation("DefaultSCFieldTitle","Default Scorecard for Call Evaluation")}</label>
                <Field
                  id="scorecardFormData"
                  name="scorecardFormData"
                  data={scorecards}
                  value={formRenderProps.valueGetter("scorecardFormData")}
                  textField="scorecardName"
                  filterable={true}
                  component={CustomComboBox}
                  onChange={compareChanges}
                  validator={DefaultScorecardFieldValidator}
                  style={{minHeight: '38px'}}
                />
              </div>
            </div>
            <div className="row mb-5">
              <div className="col-12">
                <div className={`assign-advanced border rounded ${advancedSettingsexpanded ? 'expanded' : 'collapsed'}`}>
                  <div onClick={() => setAdvancedSettingsexpanded(!advancedSettingsexpanded)} className="assign-advanced-title w-100 p-2 cursor-pointer d-flex align-items-center justify-content-between text-primary">
                    <h6 className="mb-0">{trans.fetchLabelKeyTranslation("AdvancedText","Advanced")}</h6>
                    <i className={`bi ${advancedSettingsexpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                  </div>
                  {advancedSettingsexpanded && (
                    <div className="assign-advanced-area p-2 border-top">
                      <div className="row mb-3">
                        <div className="col-12 d-flex align-items-end justify-content-between">
                          <div>
                            <h6 className="mb-2 font-weight-normal">{trans.fetchLabelKeyTranslation("AllocationCriteriaTextOne","Add Additional Scorecards for Evaluation")}</h6>
                            <p className="mb-0 fs-13 text-muted lh-sm">{trans.fetchLabelKeyTranslation("AllocationCriteriaTextTwo","Additional scorecards can be mapped to evaluate calls. Based on the outcome of each call, the appropriate scorecard will be assigned.")}</p>
                            <p className="mb-0 fs-13 text-muted lh-sm">{trans.fetchLabelKeyTranslation("AllocationCriteriaTextThree","You can enable existing outcome-to-scorecard mappings or")} <Button className={`btn btn-link fs-13 fw-normal p-0`} onClick={onOpenUpsertAutoQARuleModal}>+ {trans.fetchLabelKeyTranslation("AllocationCriteriaTextFour","Create New")}</Button> {trans.fetchLabelKeyTranslation("AllocationCriteriaTextFive","ones as needed")}.</p>
                          </div>
                          <div className="searchBox searchCol ml-2">
                            <CustomSearchFieldTextInput
                              className="input-search"
                              placeholder={trans.fetchLabelKeyTranslation("Search","Search")}
                              value={qaruleSearch}
                              updateValue={setQaruleSearch}
                              searchTextChangeHandler={onQaRuleSearchChange}
                              onEscapeKeyFunc={true}
                            />
                          </div>
                        </div>
                      </div>
                      {/* <div className="row mb-3">
                        <div className="col d-flex justify-content-end">
                          <div className="searchBox searchCol">
                            <CustomSearchFieldTextInput
                              className="input-search"
                              placeholder={trans.fetchLabelKeyTranslation("Search","Search")}
                              value={qaruleSearch}
                              updateValue={setQaruleSearch}
                              searchTextChangeHandler={onQaRuleSearchChange}
                              onEscapeKeyFunc={true}
                            />
                          </div>
                        </div>
                      </div> */}
                      <div className="row">
                        <div className="col-12 tableList assigned-allocation">
                          <div className="table-container table-responsive table-overflow-hidden borderLeftSecondChild table-mobile">
                            <Grid
                              data={automatedQARules}
                              // detail={DetailComponent}
                              expandField="expanded"
                              onExpandChange={handleExpandChange}
                              style={{ height: '350px' }}
                              pageable={{ pageSizes: false }}
                            >
                              <Column field="callResult.text" title={trans.fetchLabelKeyTranslation("GridCallOutcomeTitle","Call Outcomes")}/>
                              <Column field="reasonCodes" cell={CustomReasonCodesCell} title={trans.fetchLabelKeyTranslation("GridReasonCodeTitle","Reason Codes")}/>
                              <Column field="scoreCard.scorecardName" title={trans.fetchLabelKeyTranslation("GridScorecardTitle","Mapped Scorecard")} />
                              <Column title="" cell={ActionCell} width={150} />
                            </Grid>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-3 pt-2 border-top text-right ">
                <Button onClick={()=>cancelClickHandler(formRenderProps)} className="btn bg-black-5 mr-2">{trans.fetchLabelKeyTranslation("CancelBtnText","Cancel")}</Button>
              <Button
                disabled={!formRenderProps.valid || (updateUserAllocatedAutoQARulesData && !isChanged) || loader}
                onClick={()=>NewAutomatedQARuleSubmitHandler(formRenderProps)}
                className="btn bg-primary text-white me-2"
              >
                {loader ? (
                  <Loader
                    themeColor="light"
                    size="small"
                    type="infinite-spinner"
                  />
                ) : <> {trans.fetchLabelKeyTranslation("SaveBtnText","Save")} </> }
              </Button>
            </div>
          </FormElement>
        )}
      />

      {showUpsertAutoQARuleDialog && (
        <UpsertAutoQARule 
          callResults={callResults} 
          scorecards={scorecards}
          handleUpsertAutoQARuleSubmit={handleUpsertAutoQARuleSubmit}
          updateAutomatedQARuleData={updateAutomatedQARuleData}
          loader={loader}
          onCloseUpsertAutoQARuleModal={onCloseUpsertAutoQARuleModal}
        />
      )}
    </div>
  );
};

export default AssignScorecard;
